import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function TransientShaperLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/transient-shaper";
  const pageTitle = "Transient Shaper in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what a transient shaper does, how it changes attack and sustain, how it differs from compression, and how it is used on drums, guitars, vocals, and full mixes.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "transient shaper",
          "what does a transient shaper do",
          "attack and sustain",
          "transient designer",
          "transient shaping",
          "transient shaper vs compressor",
          "drum transient shaper",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Transient shaper" },
          { "@type": "Thing", name: "Attack and sustain" },
          { "@type": "Thing", name: "Envelope shaping" },
          { "@type": "Thing", name: "Compression" },
          { "@type": "Thing", name: "Dynamic processing" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Transient Shaper",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A transient shaper changes the attack and sustain of a sound, making it punchier, snappier, smoother, tighter, or more controlled.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It lets you reshape the feel of a sound without relying only on EQ, compression, or level changes.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Drums, percussion, guitars, bass, vocals, room mics, loops, sound design, and sometimes even buses or full mixes.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const transientTypes = [
    {
      name: "Classic Transient Shaper",
      description:
        "Focuses mainly on two broad controls: attack and sustain, making it easy to quickly add punch or tighten decay.",
    },
    {
      name: "Multi-Band Transient Shaper",
      description:
        "Lets you shape attack and sustain in different frequency ranges separately, which is useful for more targeted control.",
    },
    {
      name: "Frequency-Selective Transient Tool",
      description:
        "Targets only certain parts of the spectrum so you can emphasize snap, click, or body without affecting everything equally.",
    },
    {
      name: "Envelope Shaper",
      description:
        "A broader category of tools that reshape how sounds evolve over time, often beyond just simple attack and sustain controls.",
    },
    {
      name: "Transient Emphasizer",
      description:
        "Primarily boosts the leading edge of sounds to increase punch, articulation, or clarity.",
    },
    {
      name: "Transient Softener",
      description:
        "Reduces the impact of the initial hit, making sharp sounds smoother, less spiky, or less aggressive.",
    },
  ];

  const transientConcepts = [
    {
      name: "Transient",
      description:
        "The transient is the initial burst at the beginning of a sound, such as the crack of a snare, the pluck of a string, or the consonant of a vocal.",
    },
    {
      name: "Attack",
      description:
        "Attack control shapes how strong the front edge of a sound feels. More attack usually makes it seem punchier and more immediate.",
    },
    {
      name: "Sustain",
      description:
        "Sustain control shapes the body or tail after the initial hit. More sustain can make a sound feel fuller or longer, while less sustain can tighten it up.",
    },
    {
      name: "Envelope Perception",
      description:
        "A transient shaper changes the envelope of a sound, which strongly affects how energetic, aggressive, soft, tight, or roomy it feels.",
    },
  ];

  const controls = [
    {
      name: "Attack",
      description:
        "Boosts or reduces the front edge of the signal. This is often the main control for adding punch or softening sharp hits.",
    },
    {
      name: "Sustain",
      description:
        "Boosts or reduces the tail or body of the sound. Useful for tightening drums or making room mics and resonant sources feel bigger.",
    },
    {
      name: "Sensitivity / Threshold",
      description:
        "Some transient shapers let you control how strongly the processor reacts to incoming material.",
    },
    {
      name: "Output",
      description:
        "Lets you level-match after shaping the signal so you can judge the change more accurately.",
    },
    {
      name: "Mix",
      description:
        "Blends dry and processed signal together, which is useful for parallel transient shaping.",
    },
    {
      name: "Band Selection",
      description:
        "In multi-band tools, this lets you choose which frequency area you want to shape more precisely.",
    },
  ];

  const commonUses = [
    "Making kick drums hit harder without simply turning them up",
    "Adding crack and definition to snares and percussion",
    "Tightening drum room mics by reducing sustain",
    "Adding pick attack to guitars or bass",
    "Making loops feel punchier and more modern",
    "Softening overly sharp transients that feel clicky or aggressive",
  ];

  const bestPractices = [
    "Start with small moves. Transient shaping can change the feel of a sound very quickly.",
    "Level-match whenever possible so you are judging the envelope change rather than just a louder signal.",
    "Use attack and sustain together. Often the best result comes from balancing both rather than pushing one control too far.",
    "Check the sound in context. Extra attack that sounds exciting in solo can become harsh in the full mix.",
    "Try transient shaping before reaching for heavy compression if your real goal is punch or tightness rather than broader dynamic control.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compression shapes dynamics differently and why transient shapers are often compared with compressors.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "See how limiters control peaks, while transient shapers focus more on envelope character and feel.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Understand how EQ changes tone, while transient shaping changes the time-based behavior of a sound.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation adds color and density, which can complement the punch and control of transient shaping.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Transient Shaper | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Transient Shaper in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      A transient shaper is a processor that changes the envelope of a sound,
                      especially its attack and sustain. In practical terms, it can make a drum
                      hit feel punchier, a guitar pluck feel sharper, or a room mic feel tighter
                      and less washy. It is one of the most direct ways to change how a sound
                      feels without necessarily changing its pitch or tonal balance.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Transient shaping is especially useful when you like the basic tone of a
                      sound but want different impact, punch, snap, or decay. That is why it is
                      commonly used on drums and percussion, though it can also be valuable on
                      guitars, bass, loops, vocals, and even broader mix elements.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    What is a transient shaper?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    A transient shaper is a dynamics-related processor that focuses on the
                    shape of a sound over time rather than only its peak level or average
                    loudness. It is commonly used to boost or reduce the initial hit of a
                    sound and to lengthen or shorten its body afterward.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    This makes transient shaping different from tools like EQ, which changes
                    frequency balance, and different from standard compression, which reduces
                    dynamic range according to threshold, ratio, attack, and release behavior.
                    A transient shaper is often more direct when the goal is simply to make a
                    sound hit harder or ring less.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    In many cases, the benefit of a transient shaper is speed and clarity.
                    You can often get to the feel you want faster than with a compressor,
                    especially when you want more punch without a lot of extra complexity.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    How transient shaping works
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {transientConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Imagine a snare drum that has a nice tone but does not crack enough at the
                    beginning. Increasing attack with a transient shaper can make that initial
                    hit feel more defined. Or imagine a kick drum with too much lingering ring.
                    Reducing sustain can tighten the tail without needing to gate it or
                    completely redesign the sound.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Common types of transient shapers
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {transientTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Transient shaper vs compressor
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Similar goals sometimes, different approach
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        A compressor reacts to signal level and reduces dynamic range according
                        to its settings. A transient shaper focuses more directly on the shape
                        of the attack and sustain portions of a sound. Because of that, a
                        transient shaper can often feel quicker and more targeted when the goal
                        is punch, snap, or decay control.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Compressors and transient shapers are not interchangeable in every case.
                        Compression is often better for controlling overall dynamics, glue, or
                        movement. A transient shaper is often better when you want the sound to
                        hit harder, soften, tighten, or bloom differently without necessarily
                        changing everything else about it.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Common transient shaper controls
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How transient shapers are used in mixing
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      One of the most powerful uses of transient shaping is helping a sound fit
                      the arrangement. Sometimes a source does not need more EQ or more level. It
                      simply needs a different envelope. A punchier front edge can help it cut
                      through. A shorter sustain can help it stop cluttering the mix.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        When transient shaping goes too far
                      </Typography>
                    </Stack>

                    <Stack spacing={1.35}>
                      {[
                        "Too much attack can make drums or picks feel clicky, brittle, or unnaturally sharp.",
                        "Too much sustain can make a sound feel bloated, roomy, or muddy.",
                        "Too much sustain reduction can make a source feel tiny, thin, or lifeless.",
                        "Aggressive shaping across many tracks can make the whole mix feel unnatural or fatiguing.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Best practices for transient shaping
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{ ...subtlePanelSx, p: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    A transient shaper is one of the most efficient tools for changing how a
                    sound feels. It can add punch, reduce ring, soften harsh hits, or make a
                    source feel tighter and more controlled without the broader behavior of a
                    traditional compressor.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Used carefully, transient shaping can solve problems quickly and make a mix
                    feel more intentional. It is especially powerful when you already like the
                    tone of a sound and just want to change the way it hits and fades.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}